import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from '@store/useAuthStore';
import useMetadataStore from '@/store/useMetadataStore';
import useShortcutStore from '@/store/useShortcutStore';
import { CONFIG } from '@utils/constants';
import PlanetView from "@pages/PlanetView";
import './styles/buttons.css';

// Vistas y Componentes
import Login from '@brandpages/Login';
import Register from '@brandpages/Register';
import Dashboard from '@pages/Dashboard';
import Onboarding from '@pages/Onboarding';
import GameView from '@pages/GameView';
import LobbyView from '@pages/LobbyView';
import NotFound from '@pages/NotFound';
import BetaFeedback from '@pages/BetaFeedback';
import Credits from '@pages/Credits';
import MetadataError from '@pages/MetadataError';
import VerifyEmail from '@pages/VerifyEmail';
import { FeedbackPanel, ContextualMessageOverlay } from '@components/ui/general/feedback';
import ConfigModal from '@components/ui/general/ConfigModal';
import { AssetPreloadBar } from '@components/ui/general/AssetPreloadBar';
import DebugInitializer from './components/developer/utils/DebugInitializer';
import Loader from '@components/ui/general/loader/Loader';
import useSessionValidator from '@/hooks/useSessionValidator';
import useConfigModal from '@/hooks/useConfigModal';

function App() {
  const user = useAuthStore((state) => state.user);
  const players = useAuthStore((state) => state.players);
  const metadataLoaded = useMetadataStore((state) => state.loaded);
  const isAuthenticated = !!user;
  const hasPlayers = players && players.length > 0;

  // Initialize global keyboard shortcut listener
  const initShortcuts = useShortcutStore((state) => state.init);
  useEffect(() => {
    initShortcuts();
  }, [initShortcuts]);

  // Validate session on app startup
  const { isValidating } = useSessionValidator();

  // Config modal with URL sync and auto-save
  const { closeModal: closeConfigModal, isSaving: isConfigSaving } = useConfigModal();

  // Show loader while validating session
  if (isValidating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader message="Validating session..." />
      </div>
    );
  }

  // Show loader while metadata loads (safety net for session restore)
  if (isAuthenticated && !metadataLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader message="Loading game data..." />
      </div>
    );
  }

  // Helper to determine where authenticated users should go
  const getAuthenticatedRedirect = () => {
    return hasPlayers ? '/dashboard' : '/onboarding';
  };

  return (
    <>
      {CONFIG.DEBUG_MODE && <DebugInitializer />}
      <Routes>
        <Route path="/" element={isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} /> : <Login />} />
        <Route path="/register" element={isAuthenticated ? <Navigate to={getAuthenticatedRedirect()} /> : <Register />} />
        <Route path="/onboarding" element={isAuthenticated ? <Onboarding /> : <Navigate to="/" />} />
        <Route path="/metadata-error" element={<MetadataError />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/dashboard" element={isAuthenticated ? (hasPlayers ? <Dashboard /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/lobby/:gameId" element={isAuthenticated ? (hasPlayers ? <LobbyView /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/lobby/:gameId/shop" element={isAuthenticated ? (hasPlayers ? <LobbyView /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/games/:gameId" element={isAuthenticated ? (hasPlayers ? <GameView /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/planet/:planetId" element={isAuthenticated ? (hasPlayers ? <PlanetView /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/feedback" element={isAuthenticated ? (hasPlayers ? <BetaFeedback /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="/credits" element={isAuthenticated ? (hasPlayers ? <Credits /> : <Navigate to="/onboarding" />) : <Navigate to="/" />} />
        <Route path="*" element={<NotFound user={user} />} />
      </Routes>
      <FeedbackPanel />
      <ContextualMessageOverlay />
      <ConfigModal onClose={closeConfigModal} isSaving={isConfigSaving} />
      <AssetPreloadBar />
    </>
  );
}

export default App;